import { getDataFromTree } from '@apollo/client/react/ssr';
import { NextPage } from 'next';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
// Hocks
import withApollo from 'hocs/withApollo';
// Hooks
import { useGetMerchProduct } from 'hooks';
// Api
import { GET_STORE_MERCH_PRODUCT } from 'api/merch/queries';
import {
  GetStoreMerchProduct,
  GetStoreMerchProductVariables,
} from 'api/merch/types/GetStoreMerchProduct';
// Helpers
import { getCanonicalUrl } from 'helpers/canonical';
import {
  ApolloPageContext,
  PageProps,
  getCleanQueryParam,
  markPageAsNotFound,
} from 'helpers/next-page';
import {
  cleanDescription,
  generateProductStructuredData,
} from 'helpers/structured-data';
// Layouts
import Head from 'layouts/Head/Head';
import Header from 'layouts/Header/Header';
import Main from 'layouts/Main/Main';
import Footer from 'layouts/Footer/Footer';
// UI
import FourDotsLoader from 'ui/FourDotsLoader/FourDotsLoader';
// Components
import MerchComponent from 'components/Merch/Merch';
import NotFoundPage from 'components/NotFoundPage/NotFoundPage';

const MerchContent = (): JSX.Element => {
  const { asPath } = useRouter();
  const { data } = useGetMerchProduct();
  const merch = data?.getStoreMerchProduct;

  if (!merch) {
    return <FourDotsLoader />;
  }

  const merchName = merch.title || '';
  const storeName = merch.store?.storeDetails?.storeName || '';
  const metaTitle = storeName
    ? `${merchName} | Official ${storeName} Merch | MILLIONS`
    : `${merchName} | Official Merch | MILLIONS`;

  const metaDescription = (() => {
    const MAX = 160;
    const rawDesc = cleanDescription(merch.description);
    const cleanDesc = rawDesc
      .replace(/https?:\/\/\S+/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();

    const price =
      typeof merch.minPrice === 'number' && merch.minPrice > 0
        ? ` From $${merch.minPrice}.`
        : '';
    const attribution = `${
      storeName ? `by ${storeName} ` : ''
    }on MILLIONS.${price}`;

    if (cleanDesc) {
      const sentences = cleanDesc.match(/[^.!?]+[.!?]+\s*/g) || [];
      let body = '';
      for (const s of sentences) {
        const next = body ? `${body}${s}` : s.trim();
        if (`${next} ${attribution}`.length <= MAX) body = next;
        else if (!body) {
          body = s.trim();
          break;
        } else break;
      }
      if (body) return `${body.trim()} ${attribution}`.trim();
    }

    // No description — build from product type + hashtags
    const type = merch.type ? `${merch.type} ` : '';
    const tags = merch.hashtags?.length
      ? ` ${merch.hashtags.map((h) => h.name).join(', ')}.`
      : '';
    const base = `${merchName}. Official ${type}merch.${tags}`;
    return `${base} ${attribution}`.trim();
  })();

  const canonicalUrl = getCanonicalUrl(asPath);

  return (
    <>
      <Head
        title={metaTitle}
        description={metaDescription}
        image={merch.mainImageUrl || ''}
        canonicalLink={canonicalUrl}
        structuredData={generateProductStructuredData(merch, 'merch')}
      />
      <Header />
      <Main withoutSidesPadding>
        <MerchComponent merch={merch} />
      </Main>
      <Footer />
    </>
  );
};

const Merch: NextPage<PageProps> = ({ isNotFound }) => {
  if (isNotFound) {
    return <NotFoundPage />;
  }

  return <MerchContent />;
};

Merch.getInitialProps = async (ctx: ApolloPageContext): Promise<PageProps> => {
  const athleteSlug = getCleanQueryParam(ctx.query.athleteSlug);
  const productSlug = getCleanQueryParam(ctx.query.productSlug);

  if (!ctx.apolloClient || !athleteSlug || !productSlug) {
    return {};
  }

  try {
    const { data } = await ctx.apolloClient.query<
      GetStoreMerchProduct,
      GetStoreMerchProductVariables
    >({
      query: GET_STORE_MERCH_PRODUCT,
      variables: {
        input: {
          slug: productSlug,
          storeSlug: athleteSlug,
        },
      },
    });

    if (!data?.getStoreMerchProduct) {
      markPageAsNotFound(ctx);
      return { isNotFound: true };
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { section: 'ssr', page: 'merch/[productSlug]' },
    });
    markPageAsNotFound(ctx);
    return { isNotFound: true };
  }

  return {};
};

export default withApollo(Merch, { getDataFromTree });
